import { DOCUMENT } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { Router } from '@angular/router';
import { UserState } from '../../state/user-state';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Permission } from 'src/app/api/models/permission';
import { UserService } from 'src/app/service/user.service';
import { CONSOLE_VERSION } from 'src/environments/version';

/** A single left-nav entry: a section header or a navigable item. */
export interface NavItem {
  type: 'header' | 'item';
  label: string;
  url?: string;
  icon?: string;
  featureFlag?: string;
  /**
   * When set, the entry is shown only if the current user's `permissions`
   * include it. A `header` with no visible item following it is dropped too.
   */
  permission?: string;
  open?: boolean;
  /**
   * Set when the entry's URL is served by the console-svelte bundle and has
   * NO Angular counterpart, so there is nothing for the Angular router to
   * navigate to: the click must become a document request the BFF can route.
   * The mirror of `migrated` in console-svelte's `nav-items.ts`.
   *
   * Migrated pages that still have an Angular component (operational-tasks,
   * leads, pcr-tasks, outgoing-payments, settings/lead-sources) do NOT need
   * this — their Angular routes carry `StranglerGuard`, which turns the
   * in-app navigation into the same document load and keeps serving the
   * rolled-back state. Only a Svelte-only page needs the flag.
   */
  svelteOnly?: boolean;
}

@Component({
  selector: 'app-main-nav',
  templateUrl: './main-nav.component.html',
  styleUrls: ['./main-nav.component.scss']
})
export class MainNavComponent {
  /** The running bundle's version, shown muted in the nav footer. */
  readonly version = CONSOLE_VERSION;

  user$: Observable<UserState>;

  /**
   * The nav filtered to what the current user may see: permission-gated
   * entries the user lacks are removed, and any header left without a
   * following visible item is removed with them.
   */
  items$: Observable<NavItem[]>;

  /**
   * The navigation, entry for entry console-svelte's `NAV_ITEMS`: its
   * `nav-parity.spec.ts` parses this array, so add or change an entry in both
   * navs.
   */
  items: NavItem[] = [
    {
      type: 'item',
      label: 'Dashboard',
      url: '',
      icon: 'dashboard'
    },
    {
      type: 'item',
      label: 'Operational Tasks',
      url: '/operational-tasks',
      icon: 'assignment'
    },
    // Served only by console-svelte (no Angular page exists), hence
    // svelteOnly. Gated like the Svelte nav's entry: the page is for the
    // holders of reports:download, and everyone lands on the Dashboard, an
    // Angular page, so this entry is how they reach it.
    {
      type: 'item',
      label: 'Reports',
      url: '/reports',
      icon: 'summarize',
      permission: Permission.ReportsDownload,
      svelteOnly: true
    },
    {
      type: 'header',
      label: 'Invoices'
    },
    {
      type: 'item',
      label: 'Company Invoices',
      url: '/invoices',
      icon: 'search'
    },
    {
      type: 'item',
      label: 'Light Entrepreneur Invoices',
      url: '/light-entrepreneur-invoices',
      icon: 'search'
    },
    {
      type: 'item',
      label: 'Create',
      url: '/invoices/new',
      icon: 'add'
    },
    {
      type: 'item',
      label: 'Outgoing Payments',
      // Default working view: the non-successful statuses, so the link answers
      // "which of my payments have not succeeded". Single source of the default
      // filter — change here to revise it (see add-outgoing-payments-status-filter design D3).
      url: '/outgoing-payments?status=PENDING&status=IN_PROGRESS&status=FAILED',
      icon: 'payment'
    },
    {
      type: 'item',
      label: 'PCR Reports',
      url: '/pcr-tasks',
      icon: 'assessment'
    },
    {
      type: 'header',
      label: 'End Customer Payments'
    },
    {
      type: 'item',
      label: 'Search',
      url: '/payment-histories',
      icon: 'search'
    },
    {
      type: 'item',
      label: 'Search (new)',
      url: '/incoming-payments',
      icon: 'search'
    },
    {
      type: 'item',
      label: 'Import',
      url: '/payment-histories/import',
      icon: 'get_app'
    },
    {
      type: 'header',
      label: 'Leads'
    },
    {
      type: 'item',
      label: 'Search',
      url: '/leads',
      icon: 'search'
    },
    {
      type: 'header',
      label: 'Clients',
    },
    {
      type: 'item',
      label: 'Search',
      url: '/clients',
      icon: 'search',
    },
    {
      type: 'header',
      label: 'End Customers',
      permission: Permission.EndCustomersMerge,
    },
    {
      type: 'item',
      label: 'Duplicates',
      url: '/end-customers/duplicates',
      icon: 'call_merge',
      permission: Permission.EndCustomersMerge,
    },
    {
      type: 'header',
      label: 'Settings'
    },
    {
      type: 'item',
      label: 'Settings',
      url: '/settings',
      icon: 'settings'
    },
    // Served only by console-svelte (no Angular page exists), hence
    // svelteOnly. Ungated on purpose: the role catalog is internal
    // documentation, and whoever assigns roles is not necessarily the person
    // holding the most permissions.
    {
      type: 'item',
      label: 'Roles',
      url: '/settings/roles',
      icon: 'verified_user',
      svelteOnly: true
    }
  ];

  constructor(private router: Router,
    private userService: UserService,
    @Inject(DOCUMENT) private document: Document) {
    this.user$ = this.userService.user$;
    this.items$ = this.user$.pipe(
      map(user => this.visibleItems(user.permissions))
    );
  }

  navigate(item: NavItem) {
    if (item.url === undefined) {
      return;
    }
    if (item.svelteOnly) {
      // Leave the Angular router: only the BFF decides which bundle owns a
      // URL, and this one has no Angular route to fall back on.
      this.document.location.assign(item.url);
      return;
    }
    this.router.navigateByUrl(item.url);
  }

  toggle(item: NavItem) {
    item.open = !item.open;
  }

  // Drop permission-gated entries the user lacks, then drop any header that no
  // longer has a visible item between it and the next header.
  private visibleItems(permissions: string[]): NavItem[] {
    const permitted = this.items.filter(
      item => item.permission === undefined || permissions.includes(item.permission)
    );
    return permitted.filter((item, index) => {
      if (item.type !== 'header') {
        return true;
      }
      for (let next = index + 1; next < permitted.length; next++) {
        if (permitted[next].type === 'header') {
          return false;
        }
        if (permitted[next].type === 'item') {
          return true;
        }
      }
      return false;
    });
  }

}
